import { createContext, useContext, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { revealReservations } from 'registry/api'

import { Reservation } from 'src/types/reservation'
import {
  GiftGiver,
  GiftGiversContextType,
  GiftGiversProviderProps,
  GiftGiversSort,
} from './GiftGiversProvider.types'

import fetchAllGiftGivers from './fetchAllGiftGivers'

const defaultSort: GiftGiversSort = {
  field: 'most_recent_reservation_at',
  direction: 'desc',
}

const GiftGiversContext = createContext<GiftGiversContextType>({
  isLoading: false,
  isFetched: false,
  giftGivers: [],
  search: null,
  sort: defaultSort,
  setSearch: (_search: string | null) => undefined,
  setSort: (_sort: GiftGiversSort) => undefined,
  removeReservation: (_reservation: Reservation) => undefined,
  revealSurprises: (_giftGiverId: string) => undefined,
})

export const GiftGiversProvider = ({
  children,
  registryId,
}: GiftGiversProviderProps) => {
  const [giftGivers, setGiftGivers] = useState<GiftGiver[]>([])
  const [dataUpdatedAt, setDataUpdatedAt] = useState<number>(0)
  const [sort, setSort] = useState<GiftGiversSort>(defaultSort)
  const [search, setSearch] = useState<string | null>(null)

  const giftGiversMap: Record<string, GiftGiver> = Object.fromEntries(
    giftGivers.map((giftGiver) => [giftGiver.id, giftGiver])
  )

  const {
    data: loadedGiftGivers,
    dataUpdatedAt: newDataUpdatedAt,
    isLoading,
    isFetched,
    refetch,
  } = useQuery({
    enabled: !!registryId,
    queryKey: [
      'REGISTRY_GIFT_GIVERS',
      registryId,
      search,
      JSON.stringify(sort),
    ],
    queryFn: () => fetchAllGiftGivers({ registryId, search, sort }),
  })

  if (isFetched && !!loadedGiftGivers && dataUpdatedAt !== newDataUpdatedAt) {
    setGiftGivers(loadedGiftGivers)
    setDataUpdatedAt(newDataUpdatedAt)
  }

  const optimisticallyUpdateGiftGiver = (updatedGiftGiver: GiftGiver) => {
    const updatedGiftGivers = giftGivers.map((gg) => {
      if (gg.id === updatedGiftGiver.id) {
        return updatedGiftGiver
      }
      return gg
    })
    const giftGiversWithReservations = updatedGiftGivers.filter(
      (gg) => gg.reservations && gg.reservations.length > 0
    )
    setGiftGivers(giftGiversWithReservations)
  }

  const optimisticallyRemoveReservation = (res: Reservation) => {
    // find gift giver that owns the reservation
    const giftGiver = giftGivers.find((gg) =>
      gg.reservations.find((r) => r.id === res.id)
    )
    if (!giftGiver) {
      return
    }

    // remove the reservation and optimistically update
    optimisticallyUpdateGiftGiver({
      ...giftGiver,
      reservations: giftGiver.reservations.filter((r) => r.id !== res.id),
    })
  }

  const removeReservation = (res: Reservation) => {
    optimisticallyRemoveReservation(res)
    refetch()
  }

  const optimisticallyReveal = (giftGiver: GiftGiver) => {
    const updatedGiftGiver = {
      ...giftGiver,
      areAllSurprisesRevealedForUser: true,
      reservations: giftGiver.reservations.map((reservation) => ({
        ...reservation,
        isSurpriseRevealedForUser: true,
      })),
    }
    optimisticallyUpdateGiftGiver(updatedGiftGiver)
  }

  const apiReveal = (giftGiver: GiftGiver) => {
    const unrevealedSurpriseTokens = giftGiver.reservations
      .filter(
        (reservation) =>
          !!reservation.isSurprise && !reservation.isSurpriseRevealedForUser
      )
      .map((reservation) => reservation.token)
      .filter((token): token is string => !!token)
    return revealReservations(unrevealedSurpriseTokens)
  }

  const revealSurprises = (giftGiverId: string) => {
    const giftGiver = giftGiversMap[giftGiverId]
    optimisticallyReveal(giftGiver)
    apiReveal(giftGiver).then(() => refetch())
  }

  const value = {
    isLoading,
    isFetched,
    giftGivers,
    search,
    sort,
    setSearch,
    setSort,
    revealSurprises,
    removeReservation,
  }

  return (
    <GiftGiversContext.Provider value={value}>
      {children}
    </GiftGiversContext.Provider>
  )
}

export const useGiftGivers = () => useContext(GiftGiversContext)
